import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Mic, Sparkles, Plus, Clock, ChevronLeft, MessageCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import Navigation from '../components/Navigation';

export default function ChatPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setMessages([{
      role: 'assistant',
      content: `I'm here, ${user?.username || 'dear one'}. Tell me what's on your mind — no structure needed. Just speak.`
    }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.username]);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const loadSessions = async () => {
    setLoadingSessions(true);
    try {
      const { data } = await api.get('/chat/sessions');
      setSessions(data.sessions || []);
    } catch (e) { console.error(e); }
    finally { setLoadingSessions(false); }
  };

  const loadSession = async (sid) => {
    try {
      const { data } = await api.get(`/chat/session/${sid}`);
      setSessionId(sid);
      setMessages(data.messages || []);
      setShowHistory(false);
    } catch (e) { console.error(e); }
  };

  const startNewChat = () => {
    setSessionId(null);
    setMessages([{
      role: 'assistant',
      content: `I'm here, ${user?.username || 'dear one'}. Tell me what's on your mind — no structure needed. Just speak.`
    }]);
    setShowHistory(false);
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const msg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setLoading(true);
    try {
      const { data } = await api.post('/chat/message', { message: msg, session_id: sessionId });
      if (!sessionId) setSessionId(data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: data.message, suggest_reading: data.suggest_reading }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', content: "I'm having trouble connecting right now. Please try again in a moment." }]);
    } finally { setLoading(false); }
  };

  const startListening = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const r = new SR(); r.continuous = false; r.interimResults = false; r.lang = 'en-US';
    r.onstart = () => setIsListening(true);
    r.onresult = (e) => setInput(prev => prev + (prev ? ' ' : '') + e.results[0][0].transcript);
    r.onerror = () => setIsListening(false); r.onend = () => setIsListening(false);
    r.start();
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } };

  return (
    <div className="min-h-screen bg-[#070A1A] flex flex-col" data-testid="chat-page">
      <Navigation />
      <div className="flex-1 flex flex-col max-w-3xl mx-auto w-full px-4 pb-24 md:pb-4">
        {/* Header */}
        <div className="flex items-center justify-between py-4">
          <div className="text-center flex-1">
            <h2 className="font-['Playfair_Display'] text-xl text-[#FFFFF0]">Talk to the Guide</h2>
            <p className="text-xs text-[#D4AF37]">A sacred conversation space</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => { setShowHistory(!showHistory); if (!showHistory) loadSessions(); }}
              data-testid="chat-history-btn"
              className="p-2 rounded-full text-[#BDB2C4] hover:text-[#D4AF37] hover:bg-white/5 transition-all" title="Chat History">
              <Clock size={18} />
            </button>
            <button onClick={startNewChat} data-testid="chat-new-btn"
              className="p-2 rounded-full text-[#BDB2C4] hover:text-[#D4AF37] hover:bg-white/5 transition-all" title="New Chat">
              <Plus size={18} />
            </button>
          </div>
        </div>

        {/* Chat History Panel */}
        <AnimatePresence>
          {showHistory && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden mb-4" data-testid="chat-history-panel">
              <div className="glass-card p-4 max-h-60 overflow-y-auto">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm text-[#FFFFF0] font-medium">Past Conversations</h3>
                  <button onClick={() => setShowHistory(false)} className="text-[#BDB2C4] hover:text-[#FFFFF0]">
                    <ChevronLeft size={16} />
                  </button>
                </div>
                {loadingSessions ? (
                  <div className="flex justify-center py-4"><div className="w-5 h-5 border-2 border-[#D4AF37]/30 border-t-[#D4AF37] rounded-full animate-spin" /></div>
                ) : sessions.length === 0 ? (
                  <p className="text-xs text-[#BDB2C4] text-center py-3">No past conversations yet</p>
                ) : (
                  <div className="space-y-2">
                    {sessions.map(s => (
                      <button key={s.session_id} onClick={() => loadSession(s.session_id)} data-testid={`session-${s.session_id}`}
                        className={`w-full text-left p-3 rounded-lg transition-all hover:bg-white/5 ${sessionId === s.session_id ? 'bg-white/10 border border-[#D4AF37]/20' : ''}`}>
                        <div className="flex items-center gap-2">
                          <MessageCircle size={12} className="text-[#D4AF37] shrink-0" />
                          <p className="text-xs text-[#FFFFF0] truncate">{s.last_message?.substring(0, 60)}...</p>
                        </div>
                        <p className="text-[10px] text-[#BDB2C4] mt-1 ml-5">{s.message_count} messages</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto space-y-4 py-4" data-testid="chat-messages">
          {messages.map((msg, i) => (
            <motion.div key={msg.id || `${msg.role}-${msg.created_at || i}`} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-2xl px-5 py-3 ${msg.role === 'user' ? 'bg-[#D4AF37]/10 border border-[#D4AF37]/20 text-[#FFFFF0]' : 'glass-card text-[#BDB2C4]'}`}
                data-testid={`chat-message-${i}`}>
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                {msg.suggest_reading && (
                  <button onClick={() => navigate('/home', { state: { chatSessionId: sessionId } })} className="mt-3 flex items-center gap-2 text-xs text-[#D4AF37] hover:underline" data-testid="chat-go-to-reading">
                    <Sparkles size={14} /> Go to Reading Room
                  </button>
                )}
              </div>
            </motion.div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="glass-card px-5 py-3 rounded-2xl">
                <div className="flex gap-1">
                  <span className="w-2 h-2 rounded-full bg-[#D4AF37]/50 animate-bounce" style={{animationDelay: '0s'}} />
                  <span className="w-2 h-2 rounded-full bg-[#D4AF37]/50 animate-bounce" style={{animationDelay: '0.2s'}} />
                  <span className="w-2 h-2 rounded-full bg-[#D4AF37]/50 animate-bounce" style={{animationDelay: '0.4s'}} />
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="glass-card p-3 flex items-end gap-2" data-testid="chat-input-area">
          <textarea ref={inputRef} data-testid="chat-input" value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown}
            placeholder="Share what's on your mind..." rows={1}
            className="flex-1 bg-transparent text-[#FFFFF0] placeholder:text-[#BDB2C4]/40 resize-none focus:outline-none text-sm py-2" />
          <button onClick={startListening} data-testid="chat-mic-btn"
            className={`p-2 rounded-full transition-all ${isListening ? 'bg-[#C87886]/30 text-[#C87886] animate-pulse' : 'text-[#BDB2C4] hover:text-[#D4AF37]'}`}>
            <Mic size={18} />
          </button>
          <button onClick={sendMessage} disabled={!input.trim() || loading} data-testid="chat-send-btn"
            className="p-2 rounded-full bg-[#D4AF37]/20 text-[#D4AF37] hover:bg-[#D4AF37]/30 transition-all disabled:opacity-30">
            <Send size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
